import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box} from "@mui/system";
import {Autocomplete, CircularProgress, Divider, TextField, Typography} from "@mui/material";
import _ from "lodash";
import {organizationSettingsService} from "_services";
import {alertMessages} from "_constants";
import {Auth} from "components/Authorization";
import {SnackBar} from "components/common";
import ActionButton from "../../../common/ActionButton";
import BasicModal from "../../../common/BasicModal";
import styles from "../organizationMainStyles";
import {fragmentId} from "../Utilities/organizationSettingsConstants";
import {getLabel} from "../Utilities/organizationSettingsUtils";

const ERROR_COLOR = "#D32F2F";

// Variant of the prompt used when there is no more specific one, mirrors the server's SuggestedResponsePromptVariant.DEFAULT
const DEFAULT_VARIANT = "default";

// Buttons fit their labels instead of filling the row
const buttonSx = {width: "auto", minWidth: "110px"};

const removeButtonSx = {
    ...buttonSx,
    backgroundColor: ERROR_COLOR,
    ":hover": {
        backgroundColor: "#B71C1C",
        color: "#fff",
    },
    // ActionButton is disabled while loading, so keep it red behind the spinner
    ":disabled": {
        backgroundColor: `${ERROR_COLOR}b0`,
        color: "#fff",
    },
};

const promptInputSx = {
    "& textarea": {
        fontFamily: "larsseitRegular",
        fontSize: "15px",
    },
};

const cardSx = {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    p: 2,
    borderRadius: "5px",
    border: "1px solid #E7E9E9",
};

const cardHeadingSx = {fontFamily: "LarsseitMedium", fontSize: "15px", color: "#0C2728"};

const comboListboxSx = {
    py: 0.5,
    "& .MuiAutocomplete-option": {
        minHeight: 30,
        py: 0.25,
        px: 1.25,
        fontSize: "0.8125rem",
        fontFamily: "LarsseitRegular",
        lineHeight: 1.2,
    },
};

// Hints sit right under the section divider, like the prompt editor
const hintSx = {...styles.infoItem, mt: 0};

// Outcomes of loading the organization's prompts
const loadStatus = {
    LOADED: "loaded",
    NOT_IN_TRENDS: "not_in_trends",
};

function formatDate(value) {
    if (!value) {
        return "—";
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function handleRequestError(error) {
    if (_.get(error, "response.status") === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, "response.data.message") || alertMessages.GENERIC_ERROR;
    SnackBar.showMessage(message);
}

/**
 * Editor of one variant's prompt: a saved one (save / delete) or a new one (save creates it / cancel)
 */
function PromptVariantCard({organizationId, label, variant, variantLabel, prompt, onSaved, onCancel, onDeleteRequest, onConflict}) {
    const [text, setText] = useState(prompt ? prompt.text : "");
    const [submitting, setSubmitting] = useState(false);

    // Show the saved text whenever the prompt is (re)loaded or saved
    useEffect(() => {
        if (prompt) {
            setText(prompt.text);
        }
    }, [prompt]);

    const isNew = prompt == null;
    const trimmedText = text.trim();
    const isDirty = isNew || text !== prompt.text;
    const showBlankError = isDirty && !trimmedText;

    const handleSave = async () => {
        setSubmitting(true);
        try {
            const res = isNew
                ? await organizationSettingsService.createSuggestedResponsePrompt(organizationId, variant, trimmedText)
                : await organizationSettingsService.updateSuggestedResponsePrompt(organizationId, variant, trimmedText);
            SnackBar.showMessage(isNew ? "Prompt created." : "Prompt saved.");
            onSaved(res.data);
        } catch (e) {
            handleRequestError(e);
            // Someone else created or deleted this variant's prompt meanwhile
            if ([404, 409].includes(_.get(e, "response.status"))) {
                onConflict();
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={cardSx}>
            <Typography sx={cardHeadingSx}>{variantLabel}</Typography>
            <TextField
                label={label.PROMPT}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={label.PROMPT_PLACEHOLDER}
                multiline
                minRows={isNew ? 8 : 4}
                fullWidth
                autoFocus={isNew}
                disabled={submitting}
                error={showBlankError}
                helperText={showBlankError ? "Prompt cannot be blank." : " "}
                // Keeps the label above the field, so the placeholder shows while it's empty and not focused
                InputLabelProps={{shrink: true, sx: {fontFamily: "LarsseitMedium", fontSize: "14px"}}}
                sx={promptInputSx}
            />
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                <Typography sx={{...styles.infoItem, m: 0}}>
                    {isNew ? label.NOT_SAVED : `${label.LAST_UPDATED}: ${formatDate(prompt.updatedAt)}`}
                </Typography>
                <Box sx={{display: "flex", gap: 1}}>
                    {isNew ? (
                        <ActionButton
                            type="button"
                            label="Cancel"
                            action={onCancel}
                            disabled={submitting}
                            withIcon={false}
                            buttonBordered
                            sx={buttonSx}
                        />
                    ) : (
                        <ActionButton
                            type="button"
                            label={label.DELETE_PROMPT}
                            action={() => onDeleteRequest(variant)}
                            disabled={submitting}
                            withIcon={false}
                            buttonBordered
                            useBorderColor={ERROR_COLOR}
                            sx={buttonSx}
                        />
                    )}
                    <ActionButton
                        type="button"
                        label="Save"
                        action={handleSave}
                        disabled={submitting || !isDirty || !trimmedText}
                        loading={submitting}
                        withIcon={false}
                        sx={buttonSx}
                    />
                </Box>
            </Box>
        </Box>
    );
}

export default function SuggestedResponses({organizationId}) {
    const label = getLabel(fragmentId.SUGGESTED_RESPONSES);

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);
    // All variants a prompt can have, in display order: Default first, then handle types
    const [variants, setVariants] = useState([]);
    // Saved prompts by their variant
    const [prompts, setPrompts] = useState({});
    // Variants added in the UI but not saved yet
    const [newVariants, setNewVariants] = useState([]);
    // Remounts the combobox to clear it after each choice
    const [comboKey, setComboKey] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const loadPrompts = useCallback(async () => {
        if (_.isEmpty(organizationId)) {
            return;
        }
        setLoading(true);
        setNewVariants([]);
        try {
            const [promptsRes, variantsRes] = await Promise.all([
                organizationSettingsService.getSuggestedResponsePrompts(organizationId),
                organizationSettingsService.getSuggestedResponsePromptVariants(),
            ]);
            setPrompts(_.keyBy(promptsRes.data, "variant"));
            setVariants(variantsRes.data);
            setStatus(loadStatus.LOADED);
        } catch (e) {
            if (_.get(e, "response.status") === 422) {
                setStatus(loadStatus.NOT_IN_TRENDS);
            } else {
                setStatus(null);
                handleRequestError(e);
            }
        } finally {
            setLoading(false);
        }
    }, [organizationId]);

    useEffect(() => {
        loadPrompts();
    }, [loadPrompts]);

    const variantLabels = useMemo(() => _.mapValues(_.keyBy(variants, "value"), "label"), [variants]);
    const shownVariants = variants.filter((v) => prompts[v.value] || newVariants.includes(v.value));
    const addableVariants = variants.filter((v) => !prompts[v.value] && !newVariants.includes(v.value));

    const addVariant = (variant) => {
        setNewVariants((current) => [...current, variant]);
        setComboKey((key) => key + 1);
    };

    const removeNewVariant = (variant) => {
        setNewVariants((current) => current.filter((v) => v !== variant));
    };

    const showSavedPrompt = (savedPrompt) => {
        setPrompts((current) => ({...current, [savedPrompt.variant]: savedPrompt}));
        removeNewVariant(savedPrompt.variant);
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await organizationSettingsService.deleteSuggestedResponsePrompt(organizationId, deleteTarget);
            SnackBar.showMessage("Prompt deleted.");
            setPrompts((current) => _.omit(current, deleteTarget));
            setDeleteTarget(null);
        } catch (e) {
            handleRequestError(e);
            if (_.get(e, "response.status") === 404) {
                setDeleteTarget(null);
                await loadPrompts();
            }
        } finally {
            setDeleting(false);
        }
    };

    const closeDeleteConfirm = () => {
        if (!deleting) {
            setDeleteTarget(null);
        }
    };

    const renderBody = () => {
        if (_.isEmpty(organizationId)) {
            return <Typography sx={hintSx}>{label.NO_ORGANIZATION_SELECTED}</Typography>;
        }
        if (loading) {
            return (
                <Box sx={styles.bodyLoading}>
                    <CircularProgress sx={styles.bodyLoadingSpinner}/>
                </Box>
            );
        }
        if (status === loadStatus.NOT_IN_TRENDS) {
            return <Typography sx={hintSx}>{label.NOT_LINKED_TO_TRENDS}</Typography>;
        }
        if (status !== loadStatus.LOADED) {
            return null;
        }
        return (
            <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                <Box sx={{maxWidth: 420}}>
                    <Autocomplete
                        key={comboKey}
                        size="small"
                        options={addableVariants}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(a, b) => a.value === b.value}
                        onChange={(event, option) => {
                            if (option?.value) {
                                addVariant(option.value);
                            }
                        }}
                        value={null}
                        disabled={_.isEmpty(addableVariants)}
                        ListboxProps={{sx: comboListboxSx}}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label={label.ADD_VARIANT}
                                placeholder={_.isEmpty(addableVariants) ? label.ALL_VARIANTS_ADDED : label.ADD_VARIANT_PLACEHOLDER}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: {fontFamily: "LarsseitRegular"},
                                }}
                                InputLabelProps={{sx: {fontFamily: "LarsseitMedium", fontSize: "0.875rem"}}}
                            />
                        )}
                    />
                </Box>
                {_.isEmpty(prompts) && <Typography sx={hintSx}>{label.DEFAULT_PROMPT_IN_USE}</Typography>}
                {shownVariants.map((v) => (
                    <PromptVariantCard
                        key={v.value}
                        organizationId={organizationId}
                        label={label}
                        variant={v.value}
                        variantLabel={v.label}
                        prompt={prompts[v.value] || null}
                        onSaved={showSavedPrompt}
                        onCancel={() => removeNewVariant(v.value)}
                        onDeleteRequest={setDeleteTarget}
                        onConflict={loadPrompts}
                    />
                ))}
            </Box>
        );
    };

    const deleteTargetLabel = variantLabels[deleteTarget] || deleteTarget;

    return (
        <Box sx={styles.sectionWrapper}>
            <Box component="h2" sx={{...styles.heading, mb: 0}}>
                {label.SUGGESTED_RESPONSES}
            </Box>
            <Divider orientation="horizontal" flexItem sx={styles.sectionDivider}/>
            {renderBody()}
            <BasicModal
                open={deleteTarget != null}
                handleClose={closeDeleteConfirm}
                title={`Delete ${deleteTargetLabel} prompt?`}
                style={{width: "440px", maxWidth: "calc(100vw - 32px)"}}
            >
                <Box sx={{display: "flex", flexDirection: "column", pt: 1}}>
                    <Typography sx={{fontFamily: "LarsseitRegular", fontSize: "0.875rem", color: "#0C2728"}}>
                        {deleteTarget === DEFAULT_VARIANT
                            ? "Without a Default variant prompt, the organization uses the global default prompt for generated replies. "
                            : `The organization's ${deleteTargetLabel} variant prompt will be deleted. `}
                        This cannot be undone.
                    </Typography>
                    <Box sx={{display: "flex", justifyContent: "flex-end", gap: 1, mt: 2}}>
                        <ActionButton
                            type="button"
                            label="Cancel"
                            action={closeDeleteConfirm}
                            disabled={deleting}
                            withIcon={false}
                            buttonBordered
                            sx={buttonSx}
                        />
                        <ActionButton
                            type="button"
                            label="Remove"
                            action={handleDelete}
                            loading={deleting}
                            withIcon={false}
                            sx={removeButtonSx}
                        />
                    </Box>
                </Box>
            </BasicModal>
        </Box>
    );
}
